<template>
  <MainLayout>
    <div class="rewards-content">
      <!-- User Points Section -->
      <v-card elevation="2" class="mb-6">
        <v-card-text class="d-flex justify-space-between align-center">
          <h2 class="text-h5 font-weight-bold">Your Tokens</h2>
          <div class="text-h6 text-primary font-weight-medium">
            <span class="mr-1">{{ workspaceEmoji.emojiSymbol || '🙏' }}</span>
            {{ stats.redeemablePoints }} tokens available
          </div>
        </v-card-text>
      </v-card>

      <!-- Store Section -->
      <v-card elevation="2">
        <v-card-title class="d-flex justify-space-between align-center pa-4">
          <div>
            <span class="text-h5 font-weight-bold">Rewards Store</span>
          </div>
        </v-card-title>

        <v-card-text>
          <!-- Loading State -->
          <div v-if="loading" class="d-flex justify-center align-center py-8">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>

          <!-- Error State -->
          <v-alert
            v-else-if="error"
            type="error"
            class="my-4"
          >
            {{ error }}
          </v-alert>

          <!-- Store Items Grid -->
          <template v-if="!loading && !error">
            <div v-if="storeItems.length === 0" class="d-flex flex-column align-center justify-center py-12">
              <v-icon size="48" color="grey-lighten-1" class="mb-4">mdi-gift-off-outline</v-icon>
              <div class="text-h6 text-medium-emphasis mb-2">No rewards available</div>
              <div class="text-body-2 text-grey">Check back later for new rewards to claim!</div>
            </div>
            <v-row v-else>
              <v-col
                v-for="item in storeItems"
                :key="item.id"
                cols="12"
                sm="12"
                md="6"
              >
                <v-card
                  elevation="1"
                  class="h-100"
                  rounded="lg"
                  :hover="true"
                >
                  <v-img
                    v-if="item.imageUrl"
                    :src="item.imageUrl"
                    :alt="item.name"
                    height="200"
                    cover
                    class="bg-grey-lighten-2"
                  ></v-img>
                  <v-img
                    v-else
                    height="200"
                    cover
                    class="bg-grey-lighten-2"
                  >
                    <div class="d-flex align-center justify-center fill-height">
                      <v-icon
                        size="48"
                        color="grey-lighten-1"
                        icon="mdi-image-outline"
                      ></v-icon>
                    </div>
                  </v-img>

                  <v-card-title class="d-flex justify-space-between align-center pt-4 px-4">
                    <span class="text-truncate">{{ item.name }}</span>
                  </v-card-title>

                  <v-card-text class="pt-2">
                    <p class="text-body-2 text-medium-emphasis mb-3 text-truncate-2-lines">
                      {{ item.description }}
                    </p>
                    <div class="d-flex justify-space-between align-center">
                      <v-chip
                        color="primary"
                        variant="flat"
                        size="small"
                        class="font-weight-medium"
                      >
                        <template v-slot:prepend>
                          <span class="mr-1">{{ workspaceEmoji.emojiSymbol || '🙏' }}</span>
                        </template>
                        {{ item.points }} tokens
                      </v-chip>
                      <v-chip
                        color="grey"
                        variant="flat"
                        size="small"
                        class="font-weight-medium"
                      >
                        {{ item.quantity }} available
                      </v-chip>
                    </div>
                    <v-btn
                      block
                      color="primary"
                      class="mt-4"
                      :disabled="stats.redeemablePoints < item.points || item.quantity === 0"
                      @click="redeemItem(item)"
                    >
                      Redeem for {{ item.points }} tokens
                    </v-btn>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </template>
        </v-card-text>
      </v-card>

      <!-- Add/Edit Item Modal -->
      <v-dialog
        v-model="showAddItemModal"
        max-width="500px"
      >
        <v-card>
          <v-card-title>
            <span class="text-h5">{{ editingItem ? 'Edit' : 'Add' }} Item</span>
          </v-card-title>

          <v-card-text>
            <v-form @submit.prevent="saveItem">
              <v-text-field
                v-model="itemForm.name"
                label="Name"
                required
                variant="outlined"
                density="comfortable"
              ></v-text-field>

              <v-textarea
                v-model="itemForm.description"
                label="Description"
                variant="outlined"
                density="comfortable"
                rows="3"
              ></v-textarea>

              <v-text-field
                v-model.number="itemForm.points"
                label="Tokens"
                type="number"
                required
                min="1"
                variant="outlined"
                density="comfortable"
              ></v-text-field>

              <v-text-field
                v-model.number="itemForm.quantity"
                label="Quantity"
                type="number"
                required
                min="0"
                variant="outlined"
                density="comfortable"
              ></v-text-field>

              <v-file-input
                v-model="imageFile"
                label="Image"
                accept="image/*"
                variant="outlined"
                density="comfortable"
                prepend-icon="mdi-camera"
                :show-size="true"
                :rules="[v => !editingItem || v || imagePreview ? true : 'Image is required']"
                @change="handleImageChange"
              >
                <template v-slot:selection="{ fileNames }">
                  <template v-for="fileName in fileNames" :key="fileName">
                    <v-chip
                      size="small"
                      label
                      color="primary"
                      class="me-2"
                    >
                      {{ fileName }}
                    </v-chip>
                  </template>
                </template>
              </v-file-input>
              <div v-if="imagePreview" class="mt-2">
                <img :src="imagePreview" alt="Preview" class="preview-image" />
                <v-btn
                  v-if="imagePreview"
                  icon="mdi-close"
                  size="small"
                  color="error"
                  class="mt-2"
                  @click="clearImage"
                ></v-btn>
              </div>
            </v-form>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="grey-darken-1"
              variant="text"
              @click="showAddItemModal = false"
            >
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              variant="flat"
              @click="saveItem"
            >
              {{ editingItem ? 'Save' : 'Add' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Confirmation Modal -->
      <v-dialog
        v-model="showConfirmModal"
        max-width="500px"
      >
        <v-card>
          <v-card-title>
            <span class="text-h5">Confirm Redemption</span>
          </v-card-title>

          <v-card-text>
            Are you sure you want to redeem {{ selectedItem?.name }} for {{ selectedItem?.points }} points?
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="grey-darken-1"
              variant="text"
              @click="showConfirmModal = false"
            >
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              variant="flat"
              @click="confirmRedeem"
            >
              Confirm
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </MainLayout>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import MainLayout from './components/MainLayout.vue'
import { useStatsStore } from './stores/statsStore'
import { useWorkspaceStore } from './stores/workspaceStore'
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage'
import { storage } from './firebase'
import { useRouter } from 'vue-router'

interface StoreItem {
  id: string
  name: string
  description: string
  points: number
  quantity: number
  imageUrl: string
}

const storeItems = ref<StoreItem[]>([])
const loading = ref(true)
const error = ref('')
const showAddItemModal = ref(false)
const editingItem = ref<StoreItem | null>(null)
const itemForm = ref({
  name: '',
  description: '',
  points: 0,
  quantity: 0,
  imageUrl: ''
})
const imageFile = ref<File | null>(null)
const imagePreview = ref<string | null>(null)
const saving = ref(false)
const showConfirmModal = ref(false)
const selectedItem = ref<StoreItem | null>(null)
const router = useRouter()

const { stats, refreshStats } = useStatsStore()
const { fetchWorkspaceEmoji, getWorkspaceEmoji } = useWorkspaceStore()
const workspaceEmoji = getWorkspaceEmoji()

// Image handling functions
const handleImageChange = (file: File | null) => {
  if (!file) {
    imagePreview.value = null
    return
  }

  const reader = new FileReader()
  reader.onload = (e) => {
    imagePreview.value = e.target?.result as string
  }
  reader.readAsDataURL(file)
}

const uploadImage = async (file: File): Promise<string | null> => {
  try {
    const timestamp = Date.now()
    const fileName = `store-items/${timestamp}-${file.name}`
    const fileRef = storageRef(storage, fileName)
    
    await uploadBytes(fileRef, file)
    const downloadUrl = await getDownloadURL(fileRef)
    return downloadUrl
  } catch (error) {
    console.error('Error uploading image:', error)
    return null
  }
}

const clearImage = () => {
  imageFile.value = null
  imagePreview.value = null
  itemForm.value.imageUrl = ''
}

onMounted(async () => {
  console.log('RewardsStore component mounted')
  try {
    // Workspace emoji is already loaded at app level for authenticated users
    console.log('Loading store items...')
    await fetchStoreItems()
    console.log('Store items loaded successfully')
  } catch (error) {
    console.error('Error loading RewardsStore data:', error)
    // Continue anyway, will show error states
  }
})

async function fetchStoreItems() {
  loading.value = true
  error.value = ''
  
  try {
    console.log('Fetching store items...')
    const response = await fetch(
      `${process.env.VUE_APP_API_BASE_URL}/store/items`,
      { credentials: 'include' }
    )
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('Error response:', response.status, response.statusText, errorData)
      throw new Error(`Failed to fetch store items: ${response.status} ${response.statusText}`)
    }
    
    const items = await response.json()
    console.log('Fetched store items:', items)
    
    // Validate items structure
    if (!Array.isArray(items)) {
      console.error('Invalid items response:', items)
      throw new Error('Invalid response format')
    }
    
    storeItems.value = items.map(item => ({
      id: item.id || '',
      name: item.name || '',
      description: item.description || '',
      points: typeof item.points === 'number' ? item.points : 0,
      quantity: typeof item.quantity === 'number' ? item.quantity : 0,
      imageUrl: item.imageUrl || null
    }))
    console.log('Processed store items:', storeItems.value)
  } catch (e) {
    error.value = 'Failed to load store items. Please try again later.'
    console.error('Error fetching store items:', e)
  } finally {
    loading.value = false
  }
}

function editItem(item: StoreItem) {
  editingItem.value = item
  itemForm.value = { ...item }
  showAddItemModal.value = true
}

async function saveItem() {
  try {
    saving.value = true
    
    // Upload image if selected
    let imageUrl = itemForm.value.imageUrl
    if (imageFile.value) {
      const uploadedUrl = await uploadImage(imageFile.value)
      if (uploadedUrl) {
        imageUrl = uploadedUrl
      }
    }

    const itemData = {
      name: itemForm.value.name,
      description: itemForm.value.description,
      points: itemForm.value.points,
      quantity: itemForm.value.quantity,
      imageUrl: imageUrl
    }

    const endpoint = editingItem.value
      ? `${process.env.VUE_APP_API_BASE_URL}/store/${editingItem.value.id}`
      : `${process.env.VUE_APP_API_BASE_URL}/store`

    const method = editingItem.value ? 'PUT' : 'POST'

    const response = await fetch(endpoint, {
      method,
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(itemData)
    })

    if (!response.ok) {
      throw new Error('Failed to save item')
    }

    // Refresh store items
    await fetchStoreItems()
    showAddItemModal.value = false
    clearImage()
    itemForm.value = {
      name: '',
      description: '',
      points: 0,
      quantity: 0,
      imageUrl: ''
    }
  } catch (err) {
    console.error('Error saving item:', err)
    error.value = 'Failed to save item'
  } finally {
    saving.value = false
  }
}

function redeemItem(item: StoreItem) {
  console.log('Attempting to redeem item:', item)
  if (!item.id) {
    console.error('Item has no ID:', item)
    alert('Invalid item')
    return
  }
  selectedItem.value = item
  showConfirmModal.value = true
}

async function confirmRedeem() {
  if (!selectedItem.value?.id) {
    console.error('No valid item selected for redemption')
    return
  }
  
  try {
    console.log('Confirming redemption for item:', selectedItem.value)
    const response = await fetch(
      `${process.env.VUE_APP_API_BASE_URL}/store/items/${selectedItem.value.id}/redeem`,
      {
        method: 'POST',
        credentials: 'include'
      }
    )
    
    console.log('Redemption response:', response.status, response.statusText)
    
    const errorData = await response.json().catch(() => ({}))
    console.log('Response data:', errorData)
    
    if (!response.ok) {
      console.error('Redemption error data:', errorData)
      let errorMessage = 'Failed to redeem item'
      
      if (response.status === 401) {
        errorMessage = 'Please sign in to redeem items'
      } else if (response.status === 400) {
        errorMessage = errorData.error || 'Not enough points to redeem this item'
      } else if (response.status === 404) {
        errorMessage = 'Item not found or no longer available'
      }
      
      throw new Error(errorMessage)
    }
    
    await Promise.all([
      fetchStoreItems(),
      refreshStats() // Refresh global stats
    ])
    showConfirmModal.value = false
    selectedItem.value = null
    
    // Show success message
    alert('Item redeemed successfully!')
  } catch (e) {
    console.error('Error redeeming item:', e)
    alert(e.message || 'Failed to redeem item. Please try again.')
  }
}
</script>

<style scoped>
.rewards-content {
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
}

.preview-image {
  max-width: 200px;
  max-height: 200px;
  object-fit: cover;
  border-radius: 8px;
}
</style>
